/**
 * Polls /api/inbox/requests for this device every 30s while the wallet is open
 * and fires:
 *   1. A sonner toast (always shown when a new deposit arrives while the tab
 *      is open).
 *   2. A browser Notification API push (only if the user has granted
 *      permission — see `requestDropOffNotificationPermission()`).
 *
 * State-tracking: we keep a Set in localStorage of `${request_id}:${upload_count}`
 * so we never double-notify the same deposit revision.
 */
import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const POLL_MS = 30_000;
const NOTIFIED_KEY = "altara.notified_deposits";

export function getDeviceId() {
  let id = localStorage.getItem("altara.device_id");
  if (!id) {
    id = "dev_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36).slice(-6);
    localStorage.setItem("altara.device_id", id);
  }
  return id;
}

function loadNotifiedSet() {
  try {
    const raw = localStorage.getItem(NOTIFIED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveNotifiedSet(set) {
  try { localStorage.setItem(NOTIFIED_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}

export async function requestDropOffNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted" || Notification.permission === "denied") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

function fireBrowserPush(req) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  const who = req.depositor_name || req.institution_hint || "Your advisor";
  try {
    const n = new Notification("Document delivered to your wallet", {
      body: `${who} just delivered your ${(req.product_type || "").replace(/_/g, " ")}. NAVI is reading it now.`,
      tag: `altara-deposit-${req.id}`,
      icon: "/favicon.ico",
    });
    n.onclick = () => {
      window.focus();
      window.location.href = "/wallet/documents";
      n.close();
    };
  } catch { /* Notification constructor can throw in some browsers */ }
}

function fireInAppToast(req) {
  const who = req.depositor_name || req.institution_hint || "Your advisor";
  const productLabel = (req.product_type || "document").replace(/_/g, " ");
  toast.success(`${who} delivered your ${productLabel}`, {
    description: "It's in your wallet. NAVI is reading it now.",
    action: {
      label: "Open",
      onClick: () => { window.location.href = "/wallet/documents"; },
    },
    duration: 12_000,
  });
}

/**
 * Detect newly-received deposits since last poll.
 * A deposit is "new" if:
 *   - status == "received"
 *   - upload_count > 0
 *   - we've never notified `${id}:${upload_count}` before
 *
 * On the very first load we seed the notified-set with ALL current received
 * uploads so we don't spam past deposits.
 */
export function useDropOffPolling({ enabled = true } = {}) {
  const seededRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const device_id = getDeviceId();
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/inbox/requests?device_id=${device_id}`);
        if (cancelled) return;
        const notified = loadNotifiedSet();
        const list = Array.isArray(data) ? data : [];
        const receivedKeys = list
          .filter(r => r?.status === "received" && (r.upload_count || 0) > 0)
          .map(r => ({ req: r, key: `${r.id}:${r.upload_count}` }));

        if (!seededRef.current) {
          // First run — mark existing received uploads as already-seen so we
          // don't fire notifications for deposits that happened before the
          // wallet was opened.
          receivedKeys.forEach(({ key }) => notified.add(key));
          saveNotifiedSet(notified);
          seededRef.current = true;
          return;
        }

        const fresh = receivedKeys.filter(({ key }) => !notified.has(key));
        if (fresh.length === 0) return;
        fresh.forEach(({ req, key }) => {
          fireInAppToast(req);
          fireBrowserPush(req);
          notified.add(key);
        });
        saveNotifiedSet(notified);
      } catch { /* silent — polling should never surface network errors */ }
    };

    // Fire immediately (to seed) then poll
    tick();
    const iv = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [enabled]);
}
